/**
 * The contract status refresh (`StatutsService.setStatuts`) ages contracts into
 * passive (`!`) and expired (`!!`). From 2026-06-02 to 2026-10-26 every run
 * failed and only the server log said so, so no contract aged for almost five
 * months (bridge day check 2026-10-26, G-1). `GET /api/statuts/status` now
 * reports the latest run as `lastRun`; this decides what the Control Room shows
 * about it.
 */

export interface StatusRefreshCounts {
    active: number;
    passive: number;
    expired: number;
}

/** The latest run in the backend process, as `GET /api/statuts/status` returns it. */
export interface StatusRefreshOutcome {
    succeeded: boolean;
    startedAt: string;
    finishedAt: string;
    counts?: StatusRefreshCounts;
    error?: string;
}

/**
 * The refresh runs at startup and on the first second of each day, so a
 * successful run older than a day and a bit means the daily one did not happen.
 */
export const STATUS_REFRESH_MAX_AGE_MS = 26 * 60 * 60 * 1000;

export type StatusRefreshAlert =
    | { kind: 'failed'; finishedAt: string; error: string }
    | { kind: 'stale'; finishedAt: string };

/**
 * `null` when there is nothing to report: the last run succeeded recently, or
 * none has finished since the backend started (it runs at startup, so that is
 * only the first moments).
 */
export const selectStatusRefreshAlert = (
    lastRun: StatusRefreshOutcome | null | undefined,
    now: Date,
): StatusRefreshAlert | null => {
    if (!lastRun) {
        return null;
    }
    if (!lastRun.succeeded) {
        return { kind: 'failed', finishedAt: lastRun.finishedAt, error: lastRun.error ?? '' };
    }
    const age = now.getTime() - new Date(lastRun.finishedAt).getTime();
    return age > STATUS_REFRESH_MAX_AGE_MS
        ? { kind: 'stale', finishedAt: lastRun.finishedAt }
        : null;
};
